import { forwardRef, useCallback, useLayoutEffect, useRef } from "react";
import { Crescent } from "./crescent";
import { cx } from "@/utils/cx";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

/**
 * Gymkee 2027 buttons (specs button-primary 1.5, -secondary 1.3, -tertiary 1.4, -destructive
 * 1.5; sheet DS02). A port of coach web's atom, cut to what this app draws.
 *
 * One orange fill per page with an ink label, never white on orange. Control scale 40 and 48
 * here (32 is the desk density and nothing on a money page is that small), radius control (8).
 * Focus is a 2 px ring inside the edge, press is a scale down, disabled is 40 percent opacity
 * with the label kept, and loading draws the 16 crescent beside a label that never moves. Every
 * value comes from src/design/components.css, which is tokens only.
 *
 * A 2027 button carries a label and nothing else: an icon on its own is `IconButton`, which is
 * never orange and always has a spoken name.
 */

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "ghost"
  | "ink"
  | "destructive"
  | "link";

export type ButtonSize = "medium" | "large";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** The crescent enters beside the label; the label stays and the width is held. */
  loading?: boolean;
  /** Stretches the button to its container: the one primary at the bottom of the sheet. */
  fullWidth?: boolean;
  /** An absolute URL; the control becomes an anchor and carries rel on a new tab. */
  href?: string;
  target?: string;
  className?: string;
}

const TONE_CLASS: Record<ButtonVariant, string> = {
  primary: "ds-btn-primary",
  secondary: "ds-btn-secondary",
  tertiary: "ds-btn-tertiary",
  ghost: "ds-btn-ghost",
  ink: "ds-btn-ink",
  destructive: "ds-btn-destructive",
  link: "ds-btn-link",
};

/** The crescent's tone inside a fill, so it is never a tertiary grey on orange or on ink. */
function crescentTone(
  variant: ButtonVariant,
): "tertiary" | "onOrange" | "onInk" {
  if (variant === "primary") return "onOrange";
  if (variant === "ink" || variant === "destructive") return "onInk";
  return "tertiary";
}

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      children,
      variant = "primary",
      size = "medium",
      loading,
      fullWidth,
      href,
      target,
      className,
      disabled: _disabled,
      ...props
    },
    ref,
  ) => {
    const disabled = !!_disabled || !!loading;
    /*
     * The primary keeps its tab stop while it is unavailable.
     *
     * `disabled` on a button takes it out of the tab order entirely, so a keyboard reader tabbing
     * down the checkout went from the consent box straight past Pay to nothing, and never met the
     * caption under it that says WHY it cannot be pressed. `aria-disabled` says the same thing to
     * a screen reader, keeps the stop, and the press is refused in code instead. The fill and the
     * 40 percent ink label are unchanged, from `.ds-btn[aria-disabled="true"]`.
     *
     * Only the primary: a tertiary or a ghost that is off has nothing to explain.
     */
    const softDisabled = variant === "primary" && disabled && !loading;

    /*
     * The button keeps its width while it works (DS29). The crescent is an in-flow flex child,
     * so the moment `loading` turns true the control would grow by the glyph plus the row's gap
     * and the sheet beside it would shift. The rest width is measured on every settled render
     * and held as a floor while busy; a full-width button is unaffected.
     */
    const elementRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(
      null,
    );
    const restWidth = useRef<number | null>(null);
    useLayoutEffect(() => {
      if (!loading && elementRef.current) {
        restWidth.current = elementRef.current.getBoundingClientRect().width;
      }
    });
    const setRefs = useCallback(
      (node: HTMLButtonElement | HTMLAnchorElement | null) => {
        elementRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as { current: typeof node }).current = node;
      },
      [ref],
    );

    const classes = cx(
      "ds-btn",
      TONE_CLASS[variant],
      {
        "ds-btn-lg": size === "large",
        "ds-btn-block": fullWidth,
        "pointer-events-none": disabled,
      },
      className,
    );

    const style =
      loading && restWidth.current
        ? { minWidth: restWidth.current }
        : undefined;

    const body = (
      <>
        {/* The crescent enters at the left; the label never moves aside for it. */}
        {!!loading && (
          <Crescent size={16} tone={crescentTone(variant)} delay={0} />
        )}
        {!!children && <span className="whitespace-nowrap">{children}</span>}
      </>
    );

    if (href) {
      return (
        <a
          ref={setRefs as Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          aria-disabled={disabled || undefined}
          aria-busy={loading || undefined}
          className={classes}
          style={style}
        >
          {body}
        </a>
      );
    }

    return (
      <button
        ref={setRefs as Ref<HTMLButtonElement>}
        type="button"
        disabled={softDisabled ? undefined : disabled}
        aria-disabled={softDisabled || undefined}
        aria-busy={loading || undefined}
        className={classes}
        style={style}
        {...props}
        onClick={softDisabled ? undefined : props.onClick}
        onKeyDown={
          softDisabled
            ? (event) => {
                if (event.key === "Enter" || event.key === " ")
                  event.preventDefault();
              }
            : props.onKeyDown
        }
      >
        {body}
      </button>
    );
  },
);

Button.displayName = "Button";
